import { memo, useMemo } from 'react';
import PropTypes from 'prop-types';

import { Link2 } from 'lucide-react';

import IconHighlight from '../../../IconHighlight.jsx';
import { formatValue } from '../shared';

/**
 * How a record hangs off other records.
 *
 * Related **entities**, each with the relationship named — an employer, an
 * account owner, a deal the record takes part in, the agent that handles it.
 * Never a status: that is `ListWidget`'s job, and a relationship rendered as a
 * status badge reads as something that needs attention.
 *
 * ## Reading the contract (CT-5641)
 *
 * `CONNECTIONS` publishes `groups[]`, not a flat list: one group per relation,
 * each with its own `label`, its `count` and the `items[]` under it. The flat
 * shape could not say "2 vehicles" without repeating "Vehicles" on every row,
 * and it had no way at all to show a relation with more members than the card
 * has room for — `count` is what makes "showing 3 of 14" possible.
 *
 * ## Two lines per item, and why the second one is not noise
 *
 * The name alone — `ABC-123 · Mazda 3` — says which record, and nothing about
 * the connection. Under it go the three keys that do: `composite_code` (what
 * kind of thing it is), `relationship_label` (what it is to this record) and
 * whatever `meta` carries (its role in that relationship). They repeat the
 * group's heading on purpose: a list read out of order, or a group scrolled
 * past its header, still answers "what am I looking at".
 *
 * `since` is when the relationship started, and it gets its own column at the
 * right rather than joining that line. It is the one value here a reader
 * compares *down* the list, and a value you compare belongs in a column.
 *
 * `record_uuid` is not drawn; it is what a click carries to open the related
 * record.
 *
 * The icon travels with the group rather than being derived from the relation
 * string, because the vocabulary belongs to the tenant — one workspace's
 * "employer" is another's "parent org", and matching on text would silently
 * fall back to a generic glyph the moment someone renamed it.
 *
 * @param {object} props
 * @param {{ groups: object[] }} props.data `CONNECTIONS`' `groups[]`, or the pre-v2 `items[]`.
 * @param {(item: object, group: object) => void} [props.onItemClick]
 * @param {string} [props.className]
 */
function ConnectionsWidget({ data, onItemClick, className = '' }) {
  /*
    The pre-v2 shape was a flat `items[{id, name, relation}]`. It is read as
    one group per relation so the drawing has a single path — a flat list and a
    one-group list are the same picture.
  */
  const groups = useMemo(() => {
    if (data?.groups) {
      return data.groups.map((group, index) => ({
        key: group.key ?? group.label ?? String(index),
        label: group.label ?? '',
        count: group.count ?? group.items?.length ?? 0,
        icon: group.icon,
        items: (group.items ?? []).map((item, itemIndex) => ({
          key: item.record_uuid ?? item.label ?? String(itemIndex),
          name: item.label ?? '',
          // `meta` is an open map — whatever the relationship carries. Its
          // values join the line; its keys do not, because they are already
          // qualified by the code and the relationship beside them.
          detail: [
            item.composite_code,
            item.relationship_label,
            ...Object.values(item.meta ?? {}),
          ].filter(Boolean).map(String).join(' · '),
          since: item.since ? formatValue(item.since, { style: 'DATE' }) : '',
          raw: item,
        })),
        raw: group,
      }));
    }

    const items = data?.items ?? [];
    const byRelation = new Map();
    items.forEach((item) => {
      const relation = item.relation ?? '';
      if (!byRelation.has(relation)) {
        byRelation.set(relation, { key: relation, label: relation, icon: item.icon, items: [] });
      }
      byRelation.get(relation).items.push({
        key: item.id,
        name: item.name,
        detail: '',
        since: '',
        raw: item,
      });
    });
    return [...byRelation.values()].map((group) => ({
      ...group,
      count: group.items.length,
      raw: group,
    }));
  }, [data]);

  if (!groups.length) return null;

  return (
    <div className={`flex flex-col gap-3 ${className}`.trim()}>
      {groups.map((group) => (
        <div key={group.key} className="flex min-w-0 flex-col gap-1.5">
          <div className="flex items-baseline justify-between gap-2">
            <span className="truncate text-[10px] font-semibold uppercase tracking-[0.04em] text-content-muted">
              {group.label}
            </span>
            {/* The count is the group's, not the list's: a group may be
                truncated and the reader has to be able to tell. */}
            <span className="shrink-0 text-[10px] text-content-muted">
              {group.items.length === group.count
                ? group.count
                : `${group.items.length} / ${group.count}`}
            </span>
          </div>

          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {group.items.map((item) => {
              const body = (
                <>
                  <IconHighlight
                    icon={group.icon ?? Link2}
                    bgColor="Neutral"
                    iconColor="Primary"
                    size="small"
                  />
                  <span className="flex min-w-0 flex-1 flex-col gap-px">
                    <span className="truncate text-xs font-semibold text-content">{item.name}</span>
                    {item.detail ? (
                      <span className="truncate text-[10px] text-content-muted">{item.detail}</span>
                    ) : null}
                  </span>
                  {item.since ? (
                    <span className="shrink-0 text-[10px] text-content-faint">
                      {`since ${item.since}`}
                    </span>
                  ) : null}
                </>
              );

              return (
                <li key={item.key} className="flex min-w-0 items-center gap-2.5">
                  {onItemClick ? (
                    <button
                      type="button"
                      onClick={() => onItemClick(item.raw, group.raw)}
                      className="flex min-w-0 flex-1 items-center gap-2.5 rounded text-left hover:text-content-hover"
                    >
                      {body}
                    </button>
                  ) : (
                    body
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}

ConnectionsWidget.propTypes = {
  /** `CONNECTIONS`' `groups[{key, label, count, items}]`. Pre-v2 `items[]` still renders. */
  data: PropTypes.shape({
    groups: PropTypes.arrayOf(
      PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string,
        count: PropTypes.number,
        icon: PropTypes.elementType,
        items: PropTypes.arrayOf(
          PropTypes.shape({
            label: PropTypes.string,
            since: PropTypes.string,
            composite_code: PropTypes.string,
            relationship_label: PropTypes.string,
            /** Open map — whatever the relationship carries, e.g. `{role}`. */
            meta: PropTypes.object,
            record_uuid: PropTypes.string,
          }),
        ),
      }),
    ),
    /** The pre-v2 shape, still accepted. */
    items: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  onItemClick: PropTypes.func,
  className: PropTypes.string,
};

export default memo(ConnectionsWidget);
